import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import * as walletService from '../../services/walletService'
import {
  clearAffiliateMeCache,
  getAffiliateMe,
  affiliateWithdrawRequest,
} from '../../services/affiliateService'
import type { AffiliateMeData } from '../../services/affiliateService'
import {
  affiliateWithdrawProgress,
  affiliateWithdrawShortfall,
  canAffiliateCommissionWithdraw,
  resolveMinCommissionWithdraw,
} from './affiliateConstants'
import './affiliateLayout.css'

const AffiliateWithdrawalPage: React.FC = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [affiliateData, setAffiliateData] = useState<AffiliateMeData | null>(null)
  const [amount, setAmount] = useState('')

  const loadAffiliateData = async (forceRefresh = false) => {
    if (forceRefresh) {
      clearAffiliateMeCache()
    }
    setLoading(true)
    setError(null)
    try {
      const me = await getAffiliateMe(forceRefresh)
      setAffiliateData(me)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'โหลดข้อมูลไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadAffiliateData(true)
  }, [])

  useEffect(() => {
    const refreshFromServer = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        return
      }
      void loadAffiliateData(true)
    }

    window.addEventListener('focus', refreshFromServer)
    document.addEventListener('visibilitychange', refreshFromServer)

    return () => {
      window.removeEventListener('focus', refreshFromServer)
      document.removeEventListener('visibilitychange', refreshFromServer)
    }
  }, [])

  const remaining = affiliateData?.remainingEarnings ?? 0
  const withdrawRequested = affiliateData?.payoutSummary?.withdrawRequested ?? 0
  const minCommissionWithdraw = resolveMinCommissionWithdraw(
    affiliateData?.minCommissionWithdrawalAmount,
  )
  const canWithdraw = canAffiliateCommissionWithdraw(remaining, minCommissionWithdraw)
  const shortfall = affiliateWithdrawShortfall(remaining, minCommissionWithdraw)
  const progressPct = affiliateWithdrawProgress(remaining, minCommissionWithdraw)

  const payoutMode = useMemo(() => {
    const userMode = affiliateData?.userPayoutMode
    if (userMode && userMode !== 'inherit') return userMode
    return affiliateData?.payoutMode || 'manual'
  }, [affiliateData?.payoutMode, affiliateData?.userPayoutMode])

  const payoutHint =
    payoutMode === 'auto'
      ? 'เมื่อส่งคำขอ ระบบจะโอนค่าคอมมิชชั่นเข้าเครดิตทันที จากนั้นคุณสามารถถอนเงินธนาคารได้ตามปกติ'
      : 'เมื่อส่งคำขอ ระบบจะล็อกยอดและรอแอดมินอนุมัติ จากนั้นค่าคอมมิชชั่นจะเข้าเครดิตเพื่อถอนเงินธนาคาร'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const num = parseFloat(amount.replace(/,/g, ''))
    if (Number.isNaN(num) || num < minCommissionWithdraw) {
      Swal.fire({
        icon: 'warning',
        title: 'จำนวนไม่ถูกต้อง',
        text: `กรุณาระบุจำนวนขั้นต่ำ ${minCommissionWithdraw} บาท`,
      })
      return
    }
    if (num > remaining) {
      Swal.fire({
        icon: 'warning',
        title: 'ยอดไม่เพียงพอ',
        text: `รายได้คงเหลือ ${walletService.formatCurrency(remaining)}`,
      })
      return
    }
    setSubmitting(true)
    try {
      const res = await affiliateWithdrawRequest(num)
      if (res.success && res.pendingApproval) {
        Swal.fire({
          icon: 'info',
          title: 'แจ้งเตือน',
          text: res.message || 'รออนุมัติ',
        }).then(() => {
          setAmount('')
          navigate('/affiliate/overview')
        })
      } else if (res.success) {
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: res.message || 'ค่าคอมมิชชั่นเข้าเครดิตแล้ว',
        }).then(() => {
          setAmount('')
          navigate('/affiliate/overview')
        })
      } else {
        Swal.fire({
          icon: 'info',
          title: 'แจ้งเตือน',
          text: (res as { message?: string }).message || 'ไม่สามารถดำเนินการได้ในขณะนี้',
        })
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'เกิดข้อผิดพลาด'
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: msg,
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleWithdrawAll = () => {
    const whole = Math.floor(remaining)
    setAmount(String(whole))
  }

  if (loading) {
    return (
      <div className="htd-aff__loading">
        <div className="spinner-border" role="status" />
        <p>กำลังโหลดข้อมูล...</p>
      </div>
    )
  }
  if (error) {
    return (
      <div className="htd-aff__shell">
        <h3>โหลดข้อมูลไม่สำเร็จ</h3>
        <p>{error}</p>
        <Link to="/affiliate/overview" className="htd-aff__btn htd-aff__btn--ghost mt-3" style={{ maxWidth: 280, margin: '0.85rem auto 0' }}>
          กลับไปแนะนำเพื่อน
        </Link>
      </div>
    )
  }

  return (
    <div className="htd-aff__stack">
      <section className="htd-aff__panel">
        <div className="htd-aff__hero-row">
          <div>
            <span className="htd-aff__pill">Commission Wallet</span>
            <h2 className="mt-2 mb-1" style={{ color: '#fff', fontWeight: 800, fontSize: '1.1rem' }}>
              รายได้คงเหลือ
            </h2>
            <p className="mb-0" style={{ color: 'rgba(186,230,253,0.8)', fontSize: '0.86rem' }}>
              ยอดที่พร้อมส่งคำขอถอนคอมมิชชั่น
            </p>
          </div>
          <div className="htd-aff__code-box htd-aff__code-box--bright text-center text-md-end">
            <span>ยอดคงเหลือ (บาท)</span>
            <strong className="htd-aff__amount">{walletService.formatCurrency(remaining)}</strong>
            {withdrawRequested > 0 ? (
              <div className="small mt-2" style={{ color: '#fbbf24' }}>
                รออนุมัติ {walletService.formatCurrency(withdrawRequested)} บาท
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <section className="htd-aff__panel">
        <div className={`htd-aff__status-banner ${canWithdraw ? 'is-ready' : 'is-wait'}`}>
          <span className={`htd-aff__head-icon ${canWithdraw ? 'htd-aff__head-icon--green' : 'htd-aff__head-icon--gold'}`} aria-hidden>
            <i className={`fas ${canWithdraw ? 'fa-check-circle' : 'fa-hourglass-half'}`} />
          </span>
          <div>
            <h3>{canWithdraw ? 'พร้อมส่งคำขอถอน' : 'ยังถอนไม่ได้ — ยอดยังไม่ถึงขั้นต่ำ'}</h3>
            <p>
              {canWithdraw
                ? `ถอนได้ขั้นต่ำ ${minCommissionWithdraw} บาท และไม่เกินยอดคงเหลือ`
                : `ต้องมีรายได้คงเหลืออย่างน้อย ${minCommissionWithdraw} บาท (ขาดอีก ${walletService.formatCurrency(shortfall)} บาท)`}
            </p>
          </div>
        </div>

        <div className="d-flex justify-content-between small mb-2" style={{ color: 'rgba(186,230,253,0.75)' }}>
          <span>ความคืบหน้าสู่ขั้นต่ำ {minCommissionWithdraw} บาท</span>
          <span>{progressPct.toFixed(0)}%</span>
        </div>
        <div className={`htd-aff__progress${canWithdraw ? ' is-ready' : ''}`}>
          <i style={{ width: `${progressPct}%` }} />
        </div>

        {!canWithdraw ? (
          <div className="htd-aff__tips">
            <strong>
              <i className="fas fa-lightbulb me-2" style={{ color: '#fbbf24' }} />
              วิธีเพิ่มรายได้คอมมิชชั่น
            </strong>
            <ul>
              <li>แชร์ลิงก์แนะนำเพื่อนจากหน้าภาพรวม</li>
              <li>รับคอมมิชชั่นเมื่อสมาชิกที่แนะนำเล่นและรอบ/เกมสรุปผลแล้ว</li>
              <li>เมื่อยอดถึง {minCommissionWithdraw} บาท ปุ่มส่งคำขอถอนจะเปิดให้ใช้งานทันที</li>
            </ul>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <label className="form-label mb-2" style={{ color: '#fff', fontWeight: 700 }}>
              จำนวนเงินที่ต้องการถอน (บาท)
            </label>
            <div className="htd-aff__link-row mb-2">
              <input
                type="text"
                inputMode="numeric"
                className="htd-aff__input htd-aff__input--bright"
                placeholder={`ขั้นต่ำ ${minCommissionWithdraw} บาท`}
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/\D/g, ''))}
              />
              <button type="button" className="htd-aff__copy-btn" onClick={handleWithdrawAll}>
                ถอนทั้งหมด
              </button>
            </div>
            <p className="htd-aff__hint">
              <i className="fas fa-info-circle me-1" />
              {payoutHint}
            </p>
            <button
              type="submit"
              className="htd-aff__btn"
              disabled={submitting || !amount}
            >
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" />
                  กำลังส่งคำขอ...
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane" />
                  ส่งคำขอถอน
                </>
              )}
            </button>
          </form>
        )}
      </section>

      <section className="htd-aff__panel">
        <div className="htd-aff__panel-head">
          <div>
            <h3>ขั้นตอนการถอนคอมมิชชั่น</h3>
            <p>จากสะสมยอดจนถึงถอนเงินธนาคาร</p>
          </div>
        </div>
        <div className="htd-aff__steps">
          {[
            { step: '1', title: 'สะสมรายได้', desc: 'รับคอมจากสมาชิกที่แนะนำ' },
            { step: '2', title: 'ส่งคำขอถอน', desc: `ยอดขั้นต่ำ ${minCommissionWithdraw} บาท` },
            {
              step: '3',
              title: payoutMode === 'auto' ? 'เข้าเครดิตอัตโนมัติ' : 'รออนุมัติ',
              desc: payoutMode === 'auto' ? 'ระบบโอนเข้าเครดิตทันที' : 'แอดมินตรวจสอบคำขอ',
            },
            { step: '4', title: 'ถอนเงินธนาคาร', desc: 'ถอนจากเมนู Wallet ตามปกติ' },
          ].map((item) => (
            <div key={item.step} className="htd-aff__step">
              <span className="htd-aff__step-num">{item.step}</span>
              <strong>{item.title}</strong>
              <span>{item.desc}</span>
            </div>
          ))}
        </div>
      </section>

      <Link to="/affiliate/overview" className="htd-aff__btn htd-aff__btn--danger">
        <i className="fas fa-arrow-left" />
        กลับไปแนะนำเพื่อน
      </Link>
    </div>
  )
}

export default AffiliateWithdrawalPage
